import React, {
  Dispatch,
  FC,
  SetStateAction,
  useState,
  FocusEvent,
} from 'react';
import styles from './DatePickerField.module.scss';
import { Col, DatePicker, Row } from 'antd';
import { ErrorMessage, Field, useField } from 'formik';
import Error from '../Error';
import moment, { Moment } from 'moment';
import { DatePickerProps } from 'enums/DatePickerEnum';
import clsx from 'clsx';
import { InfoCircleOutlined } from '@ant-design/icons';
import { RangeValue } from 'rc-picker/lib/interface';
import { getMonthName } from 'shared/utils/monthName';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';

interface DatePickerFieldProps {
  name: string;
  title?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: string | Moment | RangeValue<Moment>;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onChange?: (dates: Moment, dateString: string) => void;
  format?: string;
  label?: string;
  info?: boolean;
  infoClick?: () => void;
  disabledDate?: (current: Moment) => boolean;
  setSelectedDate: Dispatch<SetStateAction<RangeValue<Moment> | undefined>>;
  picker?: 'date' | 'week' | 'month' | 'quarter' | 'year';
  showReportingPeriod?: boolean;
  initialYear?: number;
}

const DatePickerField: FC<DatePickerFieldProps> = DatePickerFieldProps => {
  const {
    name,
    title,
    placeholder,
    onChange,
    disabled,
    format,
    label,
    info,
    infoClick,
    disabledDate,
    picker,
    showReportingPeriod,
    initialYear,
  } = DatePickerFieldProps;
  const { organizationData } = useParentOrganizationContext();

  const [field, meta] = useField(name);
  const [showLabel, setShowLabel] = useState<boolean>(false);
  const handleShowLabel = () => {
    setShowLabel(true);
  };

  const { startMonth, endMonth } = getMonthName(
    organizationData?.company?.reportingMonth ?? 1
  );

  const endYear =
    startMonth === DatePickerProps.JANUARY ? field.value : field.value + 1;

  const defaultPickerValue = moment(`${initialYear ?? moment()}-01-01`);

  return (
    <>
      {(showLabel || field.value || field.value === 0) && (
        <label
          htmlFor={name}
          className={clsx({
            [styles.labelFloat]: true,
            [styles.error]: meta.touched && meta.error,
          })}>
          {label}
        </label>
      )}
      <Field name={name}>
        {() => {
          return (
            <div className={meta.touched && meta.error ? styles.error : ''}>
              {title && <div>{title}</div>}
              <DatePicker
                format={format}
                allowClear={false}
                disabled={disabled}
                placeholder={placeholder}
                onChange={(value, dateString) => {
                  if (value) {
                    onChange?.(value, dateString);
                  }
                }}
                defaultPickerValue={defaultPickerValue}
                picker={picker}
                value={
                  field.value
                    ? moment(field.value, DatePickerProps.FORMAT_YEAR)
                    : null
                }
                disabledDate={disabledDate}
                onFocus={handleShowLabel}
              />
              {info && (
                <div
                  className={styles.infoContainer}
                  onClick={() => {
                    infoClick && infoClick();
                  }}>
                  <p>
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </p>
                </div>
              )}
              {showReportingPeriod && !!field.value ? (
                <Row className={styles.reportingPeriod}>
                  <Col
                    span={
                      24
                    }>{`Reporting Period: ${startMonth} ${field.value} - ${endMonth} ${endYear}`}</Col>
                </Row>
              ) : null}
              <ErrorMessage name={name}>
                {(message: string) => <Error message={message} />}
              </ErrorMessage>
            </div>
          );
        }}
      </Field>
    </>
  );
};

export default DatePickerField;
